<template>
  <div class="stellar-background">
    <div v-if="backgroundImage">
      <img 
        :src="backgroundImage" 
        alt="Background" 
        class="background-image" 
      />
    </div>
    <div v-else-if="backColor" class="background" :style="{ 'background-color': backColor}">
    </div>
    <div v-else>
      <img 
        src="@/assets/backgrounds/main_background_element_1080p_webp_small.webp" 
        alt="Background" 
        class="background-image" 
      />
    </div>
    <div class="overlay">
      <img 
        src="@/assets/objects/circle_element_line_light_2K.webp" 
        alt="Static Lines" 
        class="circle-lines"
      />
      <img 
        src="@/assets/objects/circle_element_circles_light_2K_3_circles.webp"
        alt="Rotating Circles" 
        class="circle-elements"
      />
      <img 
        src="@/assets/objects/circle_element_circles_light_2K_1_circle.webp"
        alt="Rotating Circles" 
        class="circle-elements-1line"
      />
      <div class="falling-star"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';

defineProps<{
  startAnimation?: boolean
  backColor?: string
  backgroundImage?: string
  circleOffset?: {
    x?: number
    y?: number
  }
}>()

let starInterval: number;
const styleSheet = ref<HTMLStyleElement | null>(null);

function createFallingStar() {
  const star = document.querySelector('.falling-star') as HTMLElement;
  if (!star) return;

  // Reset star position and remove animation
  star.style.animation = 'none';
  star.offsetHeight; // Trigger reflow

  // Random starting position (always from top of screen)
  const startX = Math.random() * window.innerWidth;
  const endX = startX + (Math.random() * 800 - 400); // Random end X position
  const duration = 2 + Math.random() * 2; // Random duration between 2-4s
  
  // Calculate angle based on trajectory
  const angle = Math.atan2(window.innerHeight, endX - startX) * (180 / Math.PI);
  
  star.style.left = `${startX}px`;
  star.style.top = '-50px';
  star.style.transform = `rotate(${angle}deg)`;
  
  // Random fade points (between 0-15% for fade in, and 40-100% for fade out)
  const fadeInEnd =  30 + Math.random() * 15;
  const fadeOutStart = 40 + Math.random() * 80;
  
  // Create unique animation name
  const animationName = 'fallingStar';
  const keyframes = `
    @keyframes ${animationName} {
      0% {
        transform: translate(0, 0) rotate(${angle}deg);
        opacity: 0;
      }
      ${fadeInEnd}% {
        transform: translate(${(endX - startX) * fadeInEnd/100}px, ${(window.innerHeight + 100) * fadeInEnd/100}px) rotate(${angle}deg);
        opacity: 0.7;
      }
      ${fadeOutStart}% {
        transform: translate(${(endX - startX) * fadeOutStart/100}px, ${(window.innerHeight + 100) * fadeOutStart/100}px) rotate(${angle}deg);
        opacity: 0.0;
      }
      100% {
        transform: translate(${endX - startX}px, ${window.innerHeight + 100}px) rotate(${angle}deg);
        opacity: 0;
      }
    }
  `;
  
  // Update or create style element
  if (!styleSheet.value) {
    styleSheet.value = document.createElement('style');
    document.head.appendChild(styleSheet.value);
  }
  styleSheet.value.textContent = keyframes;
  
  // Apply animation
  star.style.animation = `${animationName} ${duration}s linear forwards`;
  
  // Schedule next star after random interval (10-20 seconds)
  const nextInterval = 10000 + Math.random() * 10000;
  starInterval = window.setTimeout(createFallingStar, nextInterval);
}

onMounted(() => {
  createFallingStar();
});

onUnmounted(() => {
  clearTimeout(starInterval);
  // Remove the style element when component is unmounted
  if (styleSheet.value) {
    styleSheet.value.remove();
    styleSheet.value = null;
  }
});
</script>

<style scoped>
.stellar-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  z-index: 0;
}

.background-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
}

.background {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
}

.overlay {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.circle-lines,
.circle-elements,
.circle-elements-1line {
  position: absolute;
  top: v-bind('circleOffset?.y ? `calc(50% + ${circleOffset.y}px)` : "50%"');
  left: v-bind('circleOffset?.x ? `calc(50% + ${circleOffset.x}px)` : "50%"');
  transform: translate(v-bind('`calc(-50% + ${circleOffset?.x || 0}px)`'), v-bind('`calc(-50% + ${circleOffset?.y || 0}px)`')) rotate(0deg);
  background-size: contain;
}

.circle-lines {
  opacity: 0.2;
  z-index: 1;
  width: 100%;
  min-width: 2400px;
  height: auto;
}

.circle-elements {
  opacity: 0.2;
  width: 100%;
  height: auto;
  min-width: 2400px;
  z-index: 2;
  transform-origin: center;
  animation: rotate 120s linear infinite;
}

.circle-elements-1line {
  opacity: 0.2;
  width: 100%;
  height: auto;
  min-width: 2400px;
  z-index: 2;
  transform-origin: center;
  animation: rotate2 120s linear infinite;
}

.falling-star {
  position: fixed;
  width: 60px;
  height: 2px;
  background: linear-gradient(to right, #76c4ae, #76c4ae 80%, transparent);
  border-radius: 100px;
  pointer-events: none;
  z-index: 3;
}

.falling-star::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  width: 12px;
  height: 12px;
  background: radial-gradient(circle, white 0%, rgba(118, 196, 174, 0.5) 40%, transparent 70%);
  transform: translate(50%, -50%);
  border-radius: 50%;
  opacity: 0.9;
  filter: blur(1px);
  box-shadow: 0 0 10px white;
}

@keyframes rotate {
  from {
    transform: translate(v-bind('`calc(-50% + ${circleOffset?.x || 0}px)`'), v-bind('`calc(-50% + ${circleOffset?.y || 0}px)`')) rotate(0deg);
  }
  to {
    transform: translate(v-bind('`calc(-50% + ${circleOffset?.x || 0}px)`'), v-bind('`calc(-50% + ${circleOffset?.y || 0}px)`')) rotate(360deg);
  }
}

@keyframes rotate2 {
  from {
    transform: translate(v-bind('`calc(-50% + ${circleOffset?.x || 0}px)`'), v-bind('`calc(-50% + ${circleOffset?.y || 0}px)`')) rotate(0deg);
  }
  to {
    transform: translate(v-bind('`calc(-50% + ${circleOffset?.x || 0}px)`'), v-bind('`calc(-50% + ${circleOffset?.y || 0}px)`')) rotate(-360deg);
  }
}
</style>
